import {
  type ColumnsType,
  ConfirmModal,
  DEFAULT_PAGE_SIZE,
  Flex,
  Loading,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useDeleteIndicator,
  useIndicators,
} from '@/features/project/projects/detail/activities/_hooks/activity';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { formatNumber } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  DataNatureTypeLabelModifier,
  IndicatorTypeLabelModifier,
  TermTypeLabelModifier,
} from '../../../../_models/activity';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';

type Props = {
  hasEditPermission: boolean;
};

export const ActivitiesAndOutcomesTable: FC<Props> = ({
  hasEditPermission,
}) => {
  const { id: projectId } = useId();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { mutateAsync: deleteIndicatorAsync } = useDeleteIndicator();

  const [deleteIndicator, setDeleteIndicator] = useState<{
    id: string;
  } | null>(null);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: indicatorsData, isPending: isLoadingIndicators } =
    useIndicators({
      filters: {
        keyId: projectId,
        ordering: '-type,-term,sort_order',
      },
    });

  const isLoadingIndicator = !indicatorsData || isLoadingIndicators;

  if (isLoadingIndicator) {
    return <Loading />;
  }

  return (
    <Flex vertical gap={16}>
      <Flex justify="end" align="center">
        <TablePageSizeSelector
          defaultPageSize={pageSize}
          onChange={(newPageSize) => {
            setPageSize(newPageSize);
          }}
        />
      </Flex>
      <Table
        rowKey="id"
        dataSource={indicatorsData}
        columns={[
          {
            title: <Text text={t('sort-order')} />,
            key: 'sortOrder',
            dataIndex: 'sortOrder',
            render: (sortOrder) => <Text weight="regular" text={sortOrder} />,
            width: '60px',
          },
          {
            title: <Text text={t('indicator-type')} />,
            key: 'type',
            dataIndex: 'type',
            render: (type) => (
              <Text weight="regular" text={IndicatorTypeLabelModifier[type]} />
            ),
            width: '10%',
          },
          {
            title: <Text text={t('term')} />,
            key: 'term',
            dataIndex: 'term',
            render: (term) => (
              <Text
                weight="regular"
                text={term && TermTypeLabelModifier[term]}
              />
            ),
            width: '5%',
          },
          {
            title: <Text text={t('data-nature-type')} />,
            key: 'dataNatureType',
            dataIndex: 'dataNatureType',
            render: (dataNatureType) => (
              <Text
                weight="regular"
                text={
                  dataNatureType && DataNatureTypeLabelModifier[dataNatureType]
                }
              />
            ),
          },
          {
            title: <Text text={t('indicator-title')} />,
            key: 'title',
            dataIndex: 'title',
            render: (title) => <Text weight="regular" text={title} />,
            width: '15%',
          },
          {
            title: <Text text={t('indicator-description')} />,
            key: 'description',
            dataIndex: 'description',
            render: (description) => (
              <Text weight="regular" text={description} />
            ),
            width: '15%',
          },
          {
            title: <Text text={t('indicator-target-value')} />,
            key: 'targetValue',
            dataIndex: 'targetValue',
            render: (targetValue) => {
              return (
                <Text
                  weight="regular"
                  text={formatNumber(targetValue, {
                    useNegativeSign: true,
                  })}
                />
              );
            },
          },
          {
            title: <Text text={t('indicator-actual-value')} />,
            key: 'actualValue',
            dataIndex: 'actualValue',
            render: (actualValue) => (
              <Text
                weight="regular"
                text={formatNumber(actualValue, { useNegativeSign: true })}
              />
            ),
          },
          {
            title: <Text weight="regular" text={t('unit')} />,
            key: 'unit',
            dataIndex: 'unit',
            render: (unit) => <Text text={unit || null} />,
          },
          ...(hasEditPermission
            ? ([
                {
                  title: '',
                  key: 'actions',
                  render: (_, record) => (
                    <PublishedProjectMutableGuard>
                      <TableRowMenuCell
                        items={[
                          {
                            key: 'edit',
                            label: t('edit-single-indicator'),
                            onClick: () => {
                              setDrawerState({
                                open: true,
                                type: 'activity-indicator-form',
                                isEditing: true,
                                indicatorId: record.id,
                              });
                            },
                          },
                          {
                            key: 'input-target-and-actual-value',
                            label: t('input-target-and-actual-value'),
                            onClick: () => {
                              setDrawerState({
                                open: true,
                                type: 'activity-consecutive-indicator-form',
                                isEditing: true,
                                indicatorId: record.id,
                              });
                            },
                          },
                          {
                            key: 'delete',
                            label: t('delete'),
                            danger: true,
                            onClick: () => {
                              setDeleteIndicator({
                                id: record.id,
                              });
                            },
                          },
                        ]}
                      />
                    </PublishedProjectMutableGuard>
                  ),
                },
              ] satisfies ColumnsType<(typeof indicatorsData)[number]>)
            : []),
        ]}
        pagination={{
          showSizeChanger: false,
          position: ['bottomRight'],
          pageSize,
        }}
      />
      <ConfirmModal
        skipCheck
        title={t('delete')}
        open={!!deleteIndicator}
        disclaimerText={t('message-delete-indicator')}
        onCancel={() => {
          setDeleteIndicator(null);
        }}
        onOk={async () => {
          invariant(deleteIndicator, t('unreachable-code'));

          try {
            await deleteIndicatorAsync(deleteIndicator.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setDeleteIndicator(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
      />
    </Flex>
  );
};
